import { APIPath, API_IDS } from '@intigral/constant';

import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';
import { DI } from '@intigral/constant/config/DIContainer';
import { IUserRegistrationRepository } from '../../core/repositories/interfaces/iUserRegistrationRepository';
import { IHttpAdapter } from '../../core/entities/iHttpAdapter';
import {
    IConsentRequest,
    IConsentResponse,
    IPPVCountryConfigResponse,
    IPPVEventConfigResponse,
    IPPVPurchasePayload,
    IPPVPurchaseResponse,
} from '../../entity';

class UserRegistrationRepository implements IUserRegistrationRepository {
    private static instance: UserRegistrationRepository;

    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
        if (UserRegistrationRepository.instance instanceof UserRegistrationRepository) {
            return UserRegistrationRepository.instance;
        }
        UserRegistrationRepository.instance = this;
    }

    async addConsent(
        apiKey: string,
        paylod: IConsentRequest,
        token: string,
        identityToken: string
    ): Promise<IConsentResponse> {
        const response = await this.http.post<IConsentResponse>(this.apiPathManager.getPath(API_IDS.USER_CONSENT_API), {
            params: {
                apiKey,
            },
            body: {
                ...paylod,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });

        return response.data;
    }

    async getConsent(apiKey: string, token: string, identityToken: string): Promise<IConsentResponse> {
        const response = await this.http.get<IConsentResponse>(this.apiPathManager.getPath(API_IDS.USER_CONSENT_API), {
            params: {
                apiKey: apiKey,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
                'content-type': 'application/json',
            },
            body: {}, // aading because content-type is not adding in request without body
        });
        return response.data;
    }

    async getPPVEventConfig(): Promise<IPPVEventConfigResponse> {
        const response = await this.http.get<IPPVEventConfigResponse>(
            this.apiPathManager.getPath(API_IDS.PPV_GET_EVENT_CONFIG),
            {
                headers: {
                    'content-type': 'application/json',
                },
                body: {},
            }
        );
        return response.data;
    }

    async getPPVCountryConfig(threeLettersCountry = ''): Promise<IPPVCountryConfigResponse> {
        const response = await this.http.get<IPPVCountryConfigResponse>(
            this.apiPathManager.getPath(API_IDS.PPV_GET_COUNTRY_CONFIG),
            {
                headers: { 'content-type': 'application/json' },
                body: {},
                params: { threeLettersCountry },
            }
        );
        return response.data;
    }

    async ppvPurchase(
        payload: IPPVPurchasePayload,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: { [key: string]: string }
    ): Promise<IPPVPurchaseResponse> {
        const response = await this.http.post<IPPVPurchaseResponse>(this.apiPathManager.getPath(API_IDS.PPV_PURCHASE), {
            withCredentials: true,
            body: {
                ...payload,
                authToken,
                authSign,
                identityToken,
                ...csrfToken,
            },
        });
        return response.data;
    }
}

export default UserRegistrationRepository;
